import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppsIcon from '@mui/icons-material/Apps';

import { NavLink, useLocation } from 'react-router-dom';
import { useRef, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from 'src/content/auth/slice';
import { setExternalLink } from 'src/content/pages/IframeViewer/slice';
import { useNavigate } from 'react-router';
import HeaderMenuModal from 'src/content/admin/header-menu/HeaderMenuModal';
import { refreshUserMenu } from 'src/util/refreshMenu';

import {
  Box,
  List,
  ListItem,
  IconButton,
  Typography,
  ListItemText,
  useMediaQuery,
  Avatar,
  Popover,
  Divider,
  Button,
  lighten,
  Menu,
  MenuItem,
  ListSubheader,
} from '@mui/material';

const ListWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})(
  //@ts-ignore
  ({ theme, isMobile }) => `
        .MuiTouchRipple-root {
            display: none;
        }
        
        .MuiListItem-root {
            transition: ${theme.transitions.create(['color', 'fill', 'background-color'])};
            border-radius: ${theme.general.borderRadiusXXl};
            margin: ${theme.spacing(0, 0.5)};
            
            &.MuiListItem-indicators {
                padding: ${theme.spacing(1, 2)};
            
                &.active,
                &:active,
                &:hover {
                    background-color: ${isMobile ? 'transparent' : theme.colors.ckb.ternary};
                    
                    .MuiListItemText-root {
                        .MuiTypography-root {
                            color: ${theme.colors.ckb.primary};
                        }
                    }
                    
                    .MuiIconButton-root {
                        color: ${theme.colors.ckb.primary};
                    }

                    .MuiTypography-caption {
                        color: ${theme.colors.ckb.primary};
                    }
                }
            }
        }
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)}
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

// Array of Material UI colors to choose from
const colorOptions = [
  'red', 'pink', 'purple', 'deepPurple', 'indigo', 'blue',
  'lightBlue', 'cyan', 'teal', 'green', 'lightGreen', 'lime',
  'yellow', 'amber', 'orange', 'deepOrange'
];

// Function to generate a consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return '#673ab7'; // Default color (deepPurple[500])

  // Generate a number from the name string
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to pick a color
  const colorIndex = Math.abs(hash) % colorOptions.length;

  return colorOptions[colorIndex];
};

const sortSubmenus = (a: any, b: any) => {
  const orderA = a.sort_order !== undefined && a.sort_order !== null ? Number(a.sort_order) : 9999;
  const orderB = b.sort_order !== undefined && b.sort_order !== null ? Number(b.sort_order) : 9999;
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true });
};

function HeaderMenu() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //@ts-ignore
  const { name = 'User', roleMenuList = [], companyName } = useSelector((state: any) => state.Auth?.user) || {};

  // The Home top-nav item is always shown. When the role has show_home = false the
  // link still renders, but the Home page itself displays only a "Welcome, {name}"
  // screen with no data (see content/dashboards/Summary).

  const user = {
    name: name,
    // Shown only when there is exactly one Customer Group Name (Dropdown 1) company;
    // hidden for none/multiple, matching the header logo.
    jobtitle: companyName || '-'
  };

  const HIDDEN_ADMIN_ITEMS = ['Header Menu Management'];

  // A menu group is hidden from the top nav only when it has no submenus — an empty
  // group would render as a blank dropdown. Previously the "Dashboard" group was
  // hidden by name; it now shows like any other populated group.
  const hasSubmenus = (menu: any) => Array.isArray(menu.submenus) && menu.submenus.length > 0;

  // Admin menu (its submenus, minus hidden items)
  const adminMenu = useMemo(() => {
    const admin = roleMenuList.find((menu: any) => menu.menu === 'Admin');
    if (!admin) return undefined;
    return {
      ...admin,
      submenus: (admin.submenus || []).filter((s: any) => !HIDDEN_ADMIN_ITEMS.includes(s.name)),
    };
  }, [roleMenuList]);


  // Each populated role menu group (excluding Admin and special groups) becomes its own dropdown.
  // Sorted by parent_sort_order ASC.
  const VIRTUAL_PERMISSION_GROUPS = ['Tracking Cards'];
  const dashboardMenus = useMemo(() => {
    const filtered = roleMenuList.filter(
      (menu: any) => menu.menu !== 'Admin' && !VIRTUAL_PERMISSION_GROUPS.includes(menu.menu) && hasSubmenus(menu)
    );
    return [...filtered].sort((a: any, b: any) => {
      const orderA = a.parent_sort_order !== undefined && a.parent_sort_order !== null ? Number(a.parent_sort_order) : 9999;
      const orderB = b.parent_sort_order !== undefined && b.parent_sort_order !== null ? Number(b.parent_sort_order) : 9999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.menu || '').localeCompare(b.menu || '');
    });
  }, [roleMenuList]);

  // Helper to determine if current route matches any submenu in a group
  const isActive = (path: string) => location.pathname === path;
  const isGroupActive = (menuItem: any) => {
    return (menuItem?.submenus || []).some((s: any) => s.link && isActive(s.link));
  };

  // Only a few groups get their own top-level trigger; the rest collapse into
  // a single "More" dropdown so the bar never scrolls horizontally.
  const MAX_VISIBLE_MENUS = 4;
  const visibleMenus = useMemo(
    () => dashboardMenus.slice(0, MAX_VISIBLE_MENUS),
    [dashboardMenus]
  );
  const overflowMenus = useMemo(
    () => dashboardMenus.slice(MAX_VISIBLE_MENUS),
    [dashboardMenus]
  );

  // Memoize the avatar color so it doesn't change on re-renders
  const avatarColor = useMemo(() => getAvatarColor(name), [name]);
  const avatarInitial = name ? name[0] : 'U';

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  // State for additional menu dropdowns
  const [menuAnchors, setMenuAnchors] = useState<{ [key: string]: HTMLElement | null }>({});

  // Header Menu Management opens as a modal (no page navigation)
  const [headerMenuModalOpen, setHeaderMenuModalOpen] = useState<boolean>(false);

  // Sync menu permissions and order on mount
  useEffect(() => {
    refreshUserMenu();
  }, []);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/');
    handleClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuName: string) => {
    event.stopPropagation();
    const target = event.currentTarget;
    setMenuAnchors({ [menuName]: target });
  };

  const handleMenuClose = () => {
    setMenuAnchors({});
  };

  const handleSubmenuClick = (menuName: string, submenuName: string, link: string, showRedirectIcon?: boolean) => {
    handleMenuClose();
    if (!link) return;
    if (link.startsWith('/')) {
      navigate(link);
      return;
    }
    const title = `${menuName} - ${submenuName}`;
    dispatch(setExternalLink({ url: link, title, showRedirectIcon: showRedirectIcon === true }));
    navigate(`/external/${encodeURIComponent(title)}`);
  };

  const handleAdminSubmenuClick = (submenuName: string) => {
    handleMenuClose();
    // Map submenu names to routes
    if (submenuName === 'User Management') {
      navigate('/admin/users');
    } else if (submenuName === 'Role Management') {
      navigate('/admin/roles');
    } else if (submenuName === 'Menu Management') {
      navigate('/admin/menus');
    } else if (submenuName === 'Customer Management') {
      navigate('/admin/customers');
    } else if (submenuName === 'Header Menu Management') {
      setHeaderMenuModalOpen(true);
    } else if (submenuName === 'Module Card Management') {
      navigate('/admin/module-cards');
    } else if (submenuName === 'Advertisement Management') {
      navigate('/admin/advertisements');
    } else if (submenuName === 'Tracking Card Order' || submenuName === 'Tracking Card Management') {
      navigate('/admin/tracking-card-order');
    }
  };

  return (
    <>
      <ListWrapper
        //@ts-ignore
        isMobile={isMobile}
        sx={{
          display: {
            xs: 'flex',
            md: 'flex'
          },
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible',
          justifyContent: 'center',
        }}
      >
        <List
          disablePadding
          component={Box}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            // Groups beyond MAX_VISIBLE_MENUS live in the "More" dropdown,
            // so the bar never needs to scroll sideways.
            overflowX: 'hidden',
            overflowY: 'visible',
          }}
        >
          <ListItem
            button
            component={NavLink}
            to="/welcome"
            classes={{ root: 'MuiListItem-indicators' }}
          >
            {isMobile ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  sx={{
                    color: isActive('/welcome') ? theme.colors.ckb.error : 'inherit',
                  }}
                >
                  <HomeIcon />
                </IconButton>
                <Typography sx={{ fontSize: 10 }} variant="caption">Home</Typography>
              </Box>
            ) : (
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Home"
              />
            )}
          </ListItem>


          {/* One dropdown trigger per role menu group, capped at MAX_VISIBLE_MENUS */}
          {visibleMenus.map((menuItem) => {
            const isSelected = isGroupActive(menuItem);
            return (
              <ListItem
                key={menuItem.menu}
                button
                onClick={(e) => handleMenuOpen(e, menuItem.menu)}
                className={isSelected ? 'active' : ''}
                classes={{ root: 'MuiListItem-indicators' }}
              >
                {isMobile ? (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <IconButton
                      sx={{
                        color: isSelected || Boolean(menuAnchors[menuItem.menu]) ? theme.colors.ckb.primary : 'inherit',
                      }}
                    >
                      <AppsIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: 10, maxWidth: 65 }} noWrap variant="caption">{menuItem.menu}</Typography>
                  </Box>
                ) : (
                  <>
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true }}
                      primary={menuItem.menu}
                    />
                    <ExpandMoreIcon
                      sx={{
                        pointerEvents: 'none',
                        transform: menuAnchors[menuItem.menu] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: theme.transitions.create(['transform']),
                      }}
                    />
                  </>
                )}
              </ListItem>
            );
          })}

          {/* Remaining groups collapse into a single "More" trigger */}
          {!isMobile && overflowMenus.length > 0 && (
            <ListItem
              button
              onClick={(e) => handleMenuOpen(e, 'More')}
              classes={{ root: 'MuiListItem-indicators' }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="More"
              />
              <ExpandMoreIcon
                sx={{
                  pointerEvents: 'none',
                  transform: menuAnchors['More'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create(['transform']),
                }}
              />
            </ListItem>
          )}

          {/* Admin Menu - Only show if exists in roleMenuList and has visible items */}
          {!isMobile && adminMenu && adminMenu.submenus.length > 0 && (
            <ListItem
              button
              onClick={(e) => handleMenuOpen(e, 'Admin')}
              className={(isGroupActive(adminMenu) || location.pathname.startsWith('/admin')) ? 'active' : ''}
              classes={{ root: 'MuiListItem-indicators' }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true }}
                primary="Admin"
              />
              <ExpandMoreIcon
                sx={{
                  pointerEvents: 'none',
                  transform: menuAnchors['Admin'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: theme.transitions.create(['transform']),
                }}
              />
            </ListItem>
          )}

          <ListItem
            button
            onClick={handleOpen}
            ref={ref}
            classes={{ root: 'MuiListItem-indicators' }}
          >
            {isMobile ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  sx={{
                    color: isOpen ? theme.colors.ckb.primary : 'inherit',
                  }}
                >
                  <PersonIcon />
                </IconButton>
                <Typography sx={{ fontSize: 10 }} variant="caption">Profile</Typography>
              </Box>
            ) : (
              <></>
            )}
          </ListItem>
        </List>
      </ListWrapper>

      {/* One dropdown per visible role menu group */}
      {visibleMenus.map((menuItem) => (
        <Menu
          key={menuItem.menu}
          anchorEl={menuAnchors[menuItem.menu]}
          open={Boolean(menuAnchors[menuItem.menu])}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 250,
            }
          }}
        >
          {[...menuItem.submenus].sort(sortSubmenus).map((submenu) => (
            <MenuItem
              key={submenu.name}
              selected={submenu.link ? isActive(submenu.link) : false}
              onClick={() => {
                handleSubmenuClick(menuItem.menu, submenu.name, submenu.link, submenu.show_redirect_icon);
                handleMenuClose();
              }}
              sx={{
                '&:hover': {
                  bgcolor: theme.colors.ckb.ternary,
                  color: theme.colors.ckb.primary,
                },
                '&.Mui-selected': {
                  bgcolor: theme.colors.ckb.ternary,
                  color: theme.colors.ckb.primary,
                  fontWeight: 'bold',
                }
              }}
            >
              {submenu.name}
            </MenuItem>
          ))}
        </Menu>
      ))}

      {/* "More" dropdown — overflow groups rendered as subheaders with their submenus */}
      {overflowMenus.length > 0 && (
        <Menu
          anchorEl={menuAnchors['More']}
          open={Boolean(menuAnchors['More'])}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 250,
              maxHeight: '70vh',
            }
          }}
        >
          {overflowMenus.flatMap((menuItem, groupIndex) => [
            groupIndex > 0 && <Divider key={`divider-${menuItem.menu}`} />,
            <ListSubheader
              key={`subheader-${menuItem.menu}`}
              disableSticky
              sx={{
                lineHeight: '32px',
                fontWeight: theme.typography.fontWeightBold,
                color: theme.colors.ckb.primary,
                bgcolor: 'transparent',
              }}
            >
              {menuItem.menu}
            </ListSubheader>,
            ...[...menuItem.submenus]
              .sort(sortSubmenus)
              .map((submenu) => (
                <MenuItem
                  key={`${menuItem.menu}-${submenu.name}`}
                  selected={submenu.link ? isActive(submenu.link) : false}
                  onClick={() => {
                    handleSubmenuClick(menuItem.menu, submenu.name, submenu.link, submenu.show_redirect_icon);
                    handleMenuClose();
                  }}
                  sx={{
                    pl: 3,
                    '&:hover': {
                      bgcolor: theme.colors.ckb.ternary,
                      color: theme.colors.ckb.primary,
                    },
                    '&.Mui-selected': {
                      bgcolor: theme.colors.ckb.ternary,
                      color: theme.colors.ckb.primary,
                      fontWeight: 'bold',
                    }
                  }}
                >
                  {submenu.name}
                </MenuItem>
              )),
          ])}
        </Menu>
      )}

      {/* Admin Menu Dropdown - Dynamically rendered from roleMenuList */}
      {adminMenu && adminMenu.submenus.length > 0 && (
        <Menu
          anchorEl={menuAnchors['Admin']}
          open={Boolean(menuAnchors['Admin'])}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
            }
          }}
        >
          {[...adminMenu.submenus].sort(sortSubmenus).map((submenu) => (
            <MenuItem
              key={submenu.name}
              onClick={() => handleAdminSubmenuClick(submenu.name)}
              sx={{
                '&:hover': {
                  bgcolor: theme.colors.ckb.ternary,
                  color: theme.colors.ckb.primary,
                }
              }}
            >
              {submenu.name}
            </MenuItem>
          ))}
        </Menu>
      )}

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox sx={{ minWidth: 210 }} display="flex">
          <Avatar sx={{ bgcolor: avatarColor }}>{avatarInitial}</Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {user.jobtitle}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider />
        <Box sx={{ m: 1 }}>
          <Button color="primary" fullWidth onClick={handleSignOut} sx={{ color: theme.colors.ckb.error }}>
            <LockOpenTwoToneIcon sx={{ mr: 1, color: theme.colors.ckb.error }} />
            Sign out
          </Button>
        </Box>
      </Popover>

      {headerMenuModalOpen && (
        <HeaderMenuModal
          open={headerMenuModalOpen}
          onClose={() => setHeaderMenuModalOpen(false)}
        />
      )}
    </>
  );
}

export default HeaderMenu;
